// Fase 23A: tipos del motor de formularios en el frontend (espejo del contrato backend). El backend
// es autoritativo; estos tipos describen lo que se envia/recibe y la logica de UI reusable.

export const QUESTION_TYPES = ['YES_NO', 'SHORT_TEXT', 'LONG_TEXT', 'NUMBER', 'SINGLE_SELECT', 'MULTI_SELECT', 'DATE', 'TIME'] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];

export const CONDITION_OPERATORS = [
  'EQUALS', 'NOT_EQUALS', 'CONTAINS', 'NOT_CONTAINS',
  'GREATER_THAN', 'GREATER_THAN_OR_EQUAL', 'LESS_THAN', 'LESS_THAN_OR_EQUAL',
  'IS_EMPTY', 'IS_NOT_EMPTY',
] as const;
export type ConditionOperator = (typeof CONDITION_OPERATORS)[number];

export type ConditionTargetType = 'QUESTION' | 'SECTION';

export const FORM_LIMITS = {
  MAX_SECTIONS: 10,
  MAX_QUESTIONS: 50,
  MAX_OPTIONS_PER_QUESTION: 20,
  MAX_QUESTION_TEXT: 300,
  MAX_CONDITIONS_PER_TARGET: 5,
} as const;

// Operadores disponibles segun el tipo de la pregunta ORIGEN (deben coincidir con el backend).
export const OPERATORS_BY_SOURCE_TYPE: Record<QuestionType, readonly ConditionOperator[]> = {
  YES_NO: ['EQUALS'],
  SINGLE_SELECT: ['EQUALS', 'NOT_EQUALS'],
  MULTI_SELECT: ['CONTAINS', 'NOT_CONTAINS'],
  NUMBER: ['EQUALS', 'GREATER_THAN', 'GREATER_THAN_OR_EQUAL', 'LESS_THAN', 'LESS_THAN_OR_EQUAL'],
  SHORT_TEXT: ['IS_EMPTY', 'IS_NOT_EMPTY'],
  LONG_TEXT: ['IS_EMPTY', 'IS_NOT_EMPTY'],
  DATE: [],
  TIME: [],
};

// Fase 23C: operadores de ATENCION por tipo (mirror backend). YES_NO EQUALS; SINGLE EQUALS; MULTI
// CONTAINS; NUMBER comparadores; TEXT IS_EMPTY/IS_NOT_EMPTY; DATE/TIME sin regla en V1.
export const ATTENTION_OPERATORS_BY_TYPE: Record<QuestionType, readonly ConditionOperator[]> = {
  YES_NO: ['EQUALS'],
  SINGLE_SELECT: ['EQUALS'],
  MULTI_SELECT: ['CONTAINS'],
  NUMBER: ['EQUALS', 'GREATER_THAN', 'GREATER_THAN_OR_EQUAL', 'LESS_THAN', 'LESS_THAN_OR_EQUAL'],
  SHORT_TEXT: ['IS_EMPTY', 'IS_NOT_EMPTY'],
  LONG_TEXT: ['IS_EMPTY', 'IS_NOT_EMPTY'],
  DATE: [],
  TIME: [],
};

export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  YES_NO: 'Si / No',
  SHORT_TEXT: 'Texto corto',
  LONG_TEXT: 'Texto largo',
  NUMBER: 'Numero',
  SINGLE_SELECT: 'Seleccion unica',
  MULTI_SELECT: 'Seleccion multiple',
  DATE: 'Fecha',
  TIME: 'Hora',
};

export const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  EQUALS: 'es igual a',
  NOT_EQUALS: 'es distinto de',
  CONTAINS: 'incluye',
  NOT_CONTAINS: 'no incluye',
  GREATER_THAN: 'es mayor que',
  GREATER_THAN_OR_EQUAL: 'es mayor o igual que',
  LESS_THAN: 'es menor que',
  LESS_THAN_OR_EQUAL: 'es menor o igual que',
  IS_EMPTY: 'esta vacia',
  IS_NOT_EMPTY: 'no esta vacia',
};

export interface FormTemplateListItem {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  storeIds: string[];
  appliesToAllStores: boolean;
  activeVersion: { id: string; versionNumber: number; publishedAt: string | null; questionCount: number } | null;
  draft: { id: string; versionNumber: number; updatedAt: string } | null;
  updatedAt: string;
}

export interface FormDraftOption {
  id: string;
  key: string;
  logicalKey?: string;
  label: string;
  position: number;
}
// Fase 23C: regla de atension (opcional) por pregunta.
export interface AttentionRuleDraft {
  operator: ConditionOperator;
  valueOptionKey?: string | null; // referencia a la opcion (por key/id) en el draft
  valueNumber?: number | null;
  valueText?: string | null;
}
export interface FormDraftQuestion {
  id: string;
  key: string;
  logicalKey?: string;
  sectionId: string;
  text: string;
  type: QuestionType;
  isRequired: boolean;
  helpText: string | null;
  position: number;
  options: FormDraftOption[];
  attention?: AttentionRuleDraft | null;
}
export interface FormDraftSection {
  id: string;
  key: string;
  title: string;
  description: string | null;
  position: number;
}
export interface FormDraftCondition {
  id: string;
  targetType: ConditionTargetType;
  targetQuestionId: string | null;
  targetSectionId: string | null;
  sourceQuestionId: string;
  operator: ConditionOperator;
  valueOptionId: string | null;
  valueNumber: number | null;
  valueText: string | null;
  position: number;
}
export interface FormDraft {
  versionId: string;
  versionNumber: number;
  status: string;
  sections: FormDraftSection[];
  questions: FormDraftQuestion[];
  conditions: FormDraftCondition[];
}

// --- Payloads de guardado (usan keys de referencia local) ---
export interface SaveConditionInput {
  sourceQuestionKey: string;
  operator: ConditionOperator;
  valueOptionKey?: string | null;
  valueNumber?: number | null;
  valueText?: string | null;
}
export interface SaveOptionInput {
  key: string;
  logicalKey?: string | null;
  label: string;
}
export interface SaveQuestionInput {
  key: string;
  logicalKey?: string | null;
  text: string;
  type: QuestionType;
  isRequired: boolean;
  helpText?: string | null;
  options: SaveOptionInput[];
  conditions: SaveConditionInput[];
  attention?: AttentionRuleDraft | null;
}
export interface SaveSectionInput {
  key: string;
  title: string;
  description?: string | null;
  conditions: SaveConditionInput[];
  questions: SaveQuestionInput[];
}
export interface SaveDraftInput {
  expectedUpdatedAt?: string;
  sections: SaveSectionInput[];
}

export interface CreateFormTemplateInput {
  name: string;
  description?: string | null;
  isActive?: boolean;
  storeIds?: string[];
}

export interface PreviewResult {
  visibleSectionIds: string[];
  visibleQuestionIds: string[];
  requiredVisibleQuestionIds: string[];
}

export type FormAnswerValue = boolean | number | string | string[] | null | undefined;
export type FormAnswers = Record<string, FormAnswerValue>;
